import { useState, type JSX, type ReactElement } from 'react';

import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import {
  EXTERNAL_LINK_AGENT_MODES,
  EXTERNAL_LINK_PROVIDERS,
  externalLinkProviderLabel,
  useCreateExternalLinkConfigMutation,
  useDeleteExternalLinkConfigMutation,
  useGetExternalLinkConfigsQuery,
  useUpdateExternalLinkConfigMutation,
  type ExternalLinkAgentMode,
  type ExternalLinkConfig,
  type ExternalLinkConfigInput,
  type ExternalLinkProviderId,
} from '../api/externalLinkConfigs';

const AGENT_MODE_LABELS: Record<ExternalLinkAgentMode, string> = {
  sales: 'Ventas',
  mortgage: 'Hipotecario',
  financial_advisor: 'Asesor financiero',
};

interface Props {
  projectId: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJsonObject = (raw: string): Record<string, unknown> | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const formatJson = (value: Record<string, unknown>): string => JSON.stringify(value, null, 2);

interface FormProps {
  projectId: number;
  config: ExternalLinkConfig | null;
  onClose: () => void;
}

const isKnownProvider = (value: string): value is ExternalLinkProviderId =>
  EXTERNAL_LINK_PROVIDERS.some((item) => item.id === value);

const ProviderPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: ExternalLinkProviderId) => void;
}): ReactElement => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = EXTERNAL_LINK_PROVIDERS.find((item) => item.id === value);
  const query = search.trim().toLowerCase();
  const options = EXTERNAL_LINK_PROVIDERS.filter(
    (item) => item.label.toLowerCase().includes(query) || item.id.includes(query)
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id="external-link-provider"
          className="w-full justify-between font-normal"
          aria-expanded={open}
        >
          <span className={cn('truncate', !selected && !value && 'text-muted-foreground')}>
            {selected?.label ?? (value || 'Selecciona un proveedor')}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) gap-0 p-0">
        <div className="flex items-center gap-2 border-b border-border-subtle px-2.5 py-1.5">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar proveedor…"
            className="h-6 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        {options.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-text-subtle">Sin resultados.</p>
        ) : (
          <ul className="flex max-h-56 flex-col gap-0.5 overflow-y-auto p-1">
            {options.map((item) => {
              const isSelected = item.id === value;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-bg-surface-alt',
                      isSelected && 'bg-bg-surface-alt font-medium'
                    )}
                    onClick={() => {
                      onChange(item.id);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <Check className={cn('size-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

const ExternalLinkConfigForm = ({ projectId, config, onClose }: FormProps): ReactElement => {
  const [provider, setProvider] = useState(config?.provider ?? '');
  const [enabled, setEnabled] = useState(config?.enabled ?? true);
  const [agentModes, setAgentModes] = useState<ExternalLinkAgentMode[]>(config?.agentModes ?? []);
  const [providerConfigText, setProviderConfigText] = useState(formatJson(config?.providerConfig ?? {}));
  const [fieldError, setFieldError] = useState<string | null>(null);

  const [createConfig, { isLoading: isCreating, error: createError }] = useCreateExternalLinkConfigMutation();
  const [updateConfig, { isLoading: isUpdating, error: updateError }] = useUpdateExternalLinkConfigMutation();
  const isSaving = isCreating || isUpdating;
  const requestError = createError ?? updateError;

  const toggleMode = (mode: ExternalLinkAgentMode, checked: boolean): void => {
    setAgentModes((current) => (checked ? [...current, mode] : current.filter((item) => item !== mode)));
  };

  const handleSubmit = async (): Promise<void> => {
    const providerConfig = parseJsonObject(providerConfigText);
    if (!isKnownProvider(provider)) {
      setFieldError('Selecciona un proveedor de la lista.');
      return;
    }
    if (agentModes.length === 0) {
      setFieldError('Selecciona al menos un modo del agente.');
      return;
    }
    if (!providerConfig) {
      setFieldError('La config del proveedor debe ser un objeto JSON.');
      return;
    }

    const body: ExternalLinkConfigInput = {
      provider,
      enabled,
      agentModes,
      providerConfig,
    };

    try {
      if (config) {
        await updateConfig({ projectId, id: config.id, body }).unwrap();
      } else {
        await createConfig({ projectId, body }).unwrap();
      }
      onClose();
    } catch {
      setFieldError(null);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{config ? 'Editar enlace externo' : 'Nuevo enlace externo'}</DialogTitle>
        <DialogDescription>La configuración aplica al proyecto master seleccionado.</DialogDescription>
      </DialogHeader>
      <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto py-2">
        <div className="grid gap-2">
          <Label htmlFor="external-link-provider">Proveedor</Label>
          <ProviderPicker value={provider} onChange={setProvider} />
        </div>
        <div className="grid gap-2">
          <p className="text-sm font-medium">Modos del agente</p>
          {EXTERNAL_LINK_AGENT_MODES.map((mode) => (
            <label key={mode} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={agentModes.includes(mode)}
                onCheckedChange={(checked) => toggleMode(mode, checked === true)}
              />
              {AGENT_MODE_LABELS[mode]}
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="external-link-enabled">Habilitado</Label>
          <Switch id="external-link-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="external-link-provider-config">Config del proveedor (JSON)</Label>
          <Textarea
            id="external-link-provider-config"
            value={providerConfigText}
            onChange={(event) => setProviderConfigText(event.target.value)}
            className="font-mono text-xs"
          />
        </div>
        {fieldError && (
          <p role="alert" className="text-sm text-error">
            {fieldError}
          </p>
        )}
        {requestError && (
          <p role="alert" className="text-sm text-error">
            {extractErrorMessage(requestError, 'No se pudo guardar el enlace externo.')}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" disabled={isSaving} onClick={onClose}>
          Cancelar
        </Button>
        <Button type="button" disabled={isSaving} onClick={() => void handleSubmit()}>
          {isSaving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogFooter>
    </>
  );
};

const ExternalLinkConfigSection = ({ projectId }: Props): JSX.Element => {
  const sectionProps = useCollapsibleSection('external-link-configs');
  const { data, isLoading, error, refetch } = useGetExternalLinkConfigsQuery(projectId);
  const [deleteConfig, { isLoading: isDeleting, error: deleteError }] = useDeleteExternalLinkConfigMutation();
  const [formConfig, setFormConfig] = useState<ExternalLinkConfig | null | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<ExternalLinkConfig | null>(null);

  const countLabel = !data
    ? null
    : data.length === 0
      ? 'Vacío'
      : data.length === 1
        ? '1 enlace'
        : `${data.length} enlaces`;

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      await deleteConfig({ projectId, id: pendingDelete.id }).unwrap();
      setPendingDelete(null);
    } catch {
      // deleteError renders inside the dialog
    }
  };

  return (
    <>
      <Accordion
        type="single"
        collapsible
        {...sectionProps}
        className="bg-bg-surface border border-border-subtle rounded-xl"
      >
        <AccordionItem value="content" className="border-none">
          <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
            <span className="flex items-center gap-3">
              <span className="font-semibold text-text-default">Enlaces externos</span>
              {countLabel && (
                <Badge
                  className={
                    data && data.length > 0 ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'
                  }
                >
                  {countLabel}
                </Badge>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
            <div className="flex flex-col gap-4 pt-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-text-muted">
                  Configura qué enlaces externos puede enviar el agente en este proyecto master.
                </p>
                <Button type="button" size="sm" onClick={() => setFormConfig(null)}>
                  Nuevo
                </Button>
              </div>
              {isLoading && <p className="text-sm text-text-subtle">Cargando enlaces...</p>}
              {error && (
                <div className="flex items-center justify-between gap-3 text-sm text-error">
                  <span>{extractErrorMessage(error, 'No se pudieron cargar los enlaces externos.')}</span>
                  <Button variant="outline" size="sm" onClick={() => void refetch()}>
                    Reintentar
                  </Button>
                </div>
              )}
              {data && data.length === 0 && (
                <p className="text-sm text-text-muted">No hay enlaces externos configurados.</p>
              )}
              {data && data.length > 0 && (
                <ul className="flex flex-col divide-y divide-border-subtle border border-border-subtle rounded-lg">
                  {data.map((config) => (
                    <li key={config.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-text-default">
                          {externalLinkProviderLabel(config.provider)}
                        </p>
                        <p className="text-xs text-text-muted">
                          {config.agentModes.map((mode) => AGENT_MODE_LABELS[mode]).join(', ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            config.enabled ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'
                          }
                        >
                          {config.enabled ? 'Habilitado' : 'Deshabilitado'}
                        </Badge>
                        <Button type="button" variant="outline" size="sm" onClick={() => setFormConfig(config)}>
                          Editar
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setPendingDelete(config)}>
                          Eliminar
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Dialog open={formConfig !== undefined} onOpenChange={(open) => !open && setFormConfig(undefined)}>
        <DialogContent className="sm:max-w-lg">
          {formConfig !== undefined && (
            <ExternalLinkConfigForm
              key={formConfig?.id ?? 'new'}
              projectId={projectId}
              config={formConfig}
              onClose={() => setFormConfig(undefined)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este enlace externo?</AlertDialogTitle>
            <AlertDialogDescription>
              Borrar la config también borra los procesos de clientes asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <p role="alert" className="text-sm text-error">
              {extractErrorMessage(deleteError, 'No se pudo eliminar el enlace externo.')}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <Button disabled={isDeleting} onClick={() => void handleDelete()}>
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExternalLinkConfigSection;
